import React, { useState } from "react";
import { httpClient } from "../../HttpClient";
import { X, Mail, KeyRound, Check, Eye, EyeOff } from "lucide-react";

// Réinitialisation en 2 étapes (email -> code reçu par mail + nouveau mot
// de passe), remplace l'ancien "Mot de passe oublié ?" qui se contentait de
// prévenir un administrateur par email (voir sendContactAdminController.js,
// toujours utilisé ailleurs si besoin). Le backend (passwordResetOtpController.js)
// ne révèle jamais si un email correspond à un compte, et rejette d'emblée
// les domaines hors cabinet (GOOGLE_ALLOWED_HD).
export default function ForgotPasswordModal({ initialEmail, onClose }) {
  const [step, setStep] = useState("email"); // email | code | done
  const [resetEmail, setResetEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendCode = async () => {
    if (!resetEmail.trim()) {
      setError("Merci de renseigner votre email.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await httpClient.post("/api/users/password-reset/request", { email: resetEmail.trim() });
      setStep("code");
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi du code.");
    } finally {
      setLoading(false);
    }
  };

  const confirmReset = async () => {
    if (!code.trim() || !newPassword || !confirmPassword) {
      setError("Merci de remplir tous les champs.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await httpClient.post("/api/users/password-reset/confirm", {
        email: resetEmail.trim(),
        code: code.trim(),
        newPassword,
      });
      setStep("done");
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la réinitialisation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-[380px] flex flex-col gap-4 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>

        {step === "email" && (
          <>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#840040]" />
              <h2 className="text-lg font-bold text-slate-900">Mot de passe oublié</h2>
            </div>
            <p className="text-sm text-slate-500">
              Indiquez votre email professionnel — un code de vérification vous sera envoyé si un compte y est associé.
            </p>
            <input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="Adresse email"
              className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg outline-none"
              onKeyDown={(e) => e.key === "Enter" && sendCode()}
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
            <button
              onClick={sendCode}
              disabled={loading}
              className="h-10 rounded-xl text-sm font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors disabled:opacity-50"
            >
              {loading ? "Envoi…" : "Envoyer le code"}
            </button>
          </>
        )}

        {step === "code" && (
          <>
            <div className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#840040]" />
              <h2 className="text-lg font-bold text-slate-900">Code de vérification</h2>
            </div>
            <p className="text-sm text-slate-500">
              Un code a été envoyé à <strong>{resetEmail}</strong> s'il correspond à un compte MyALFA. Saisissez-le
              ci-dessous avec votre nouveau mot de passe.
            </p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Code à 6 chiffres"
              maxLength={6}
              className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg outline-none tracking-[0.3em] text-center font-mono"
            />
            <div className="flex items-center border border-slate-200 rounded-lg">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nouveau mot de passe"
                className="flex-1 min-w-0 h-10 px-3 text-sm outline-none rounded-lg"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                title={showNewPassword ? "Masquer" : "Afficher"}
                className="flex-shrink-0 flex items-center px-2.5 text-slate-400 hover:text-slate-600"
              >
                {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="flex items-center border border-slate-200 rounded-lg">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmer le mot de passe"
                className="flex-1 min-w-0 h-10 px-3 text-sm outline-none rounded-lg"
                onKeyDown={(e) => e.key === "Enter" && confirmReset()}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                title={showConfirmPassword ? "Masquer" : "Afficher"}
                className="flex-shrink-0 flex items-center px-2.5 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
            <button
              onClick={confirmReset}
              disabled={loading}
              className="h-10 rounded-xl text-sm font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors disabled:opacity-50"
            >
              {loading ? "Validation…" : "Réinitialiser le mot de passe"}
            </button>
            <button onClick={() => setStep("email")} className="text-xs text-slate-400 hover:text-slate-600">
              Renvoyer un code / changer d'email
            </button>
          </>
        )}

        {step === "done" && (
          <>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-bold text-slate-900">Mot de passe réinitialisé</h2>
            </div>
            <p className="text-sm text-slate-500">
              Votre mot de passe a été mis à jour. Vous pouvez maintenant vous connecter avec votre nouveau mot de
              passe.
            </p>
            <button
              onClick={onClose}
              className="h-10 rounded-xl text-sm font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors"
            >
              Fermer
            </button>
          </>
        )}
      </div>
    </div>
  );
}
